/**
 * Core command classes + factories — every editing operation (move, resize,
 * rotate, style, lock/hide, z-order, insert/delete, duplicate/paste, align)
 * is expressed as one of three primitive commands:
 *
 * - `PatchObjectsCommand`  — next/prev property patches (the workhorse)
 * - `InsertObjectsCommand` — insert ↔ remove
 * - `RemoveObjectsCommand` — remove ↔ re-insert (captures full objects)
 *
 * plus `CompositeCommand` for batched multi-step operations. Factories
 * capture the "prev" side from the CURRENT store state at build time, so
 * `do`/`undo` are pure action calls with no state reads.
 *
 * @module floorplan/history/commands
 * @category FloorPlan
 */

import type { Vec2 } from '../models/geometry';
import type { FloorObject } from '../models/object';
import { createFloorId } from '../utils/id';
import type { CommandTarget, FloorCommand } from './types';

type ObjectsById = Readonly<Record<string, FloorObject>>;
type ObjectPatch = Partial<Omit<FloorObject, 'id'>>;

export type PatchEntry = {
  id: string;
  next: ObjectPatch;
  prev: ObjectPatch;
};

/** Deep-enough clone for command captures (style/points/metadata copied). */
const cloneObject = (object: FloorObject): FloorObject => ({
  ...object,
  style: object.style === undefined ? undefined : { ...object.style },
  points:
    object.points === undefined
      ? undefined
      : object.points.map((point) => ({ ...point })),
  metadata: { ...object.metadata },
});

/** Capture the prev side of a patch from the live object. */
const capturePrev = (object: FloorObject, next: ObjectPatch): ObjectPatch => {
  const prev: Record<string, unknown> = {};
  for (const key of Object.keys(next)) {
    const value = (object as unknown as Record<string, unknown>)[key];
    if (key === 'style' && object.style !== undefined) {
      prev[key] = { ...object.style };
    } else if (key === 'points' && object.points !== undefined) {
      prev[key] = object.points.map((point) => ({ ...point }));
    } else if (key === 'metadata') {
      prev[key] = { ...object.metadata };
    } else {
      prev[key] = value;
    }
  }
  return prev as ObjectPatch;
};

// ── Primitive commands ──────────────────────────────────────────────────────

export class PatchObjectsCommand implements FloorCommand {
  readonly label: string;

  readonly entries: readonly PatchEntry[];

  /** Two commands merge only when both carry the SAME non-null key. */
  readonly mergeKey: string | null;

  constructor(
    label: string,
    entries: readonly PatchEntry[],
    mergeKey: string | null = null,
  ) {
    this.label = label;
    this.entries = entries;
    this.mergeKey = mergeKey;
  }

  do(target: CommandTarget): void {
    target.patchObjects(
      this.entries.map((entry) => ({ id: entry.id, patch: entry.next })),
    );
  }

  undo(target: CommandTarget): void {
    target.patchObjects(
      this.entries.map((entry) => ({ id: entry.id, patch: entry.prev })),
    );
  }

  merge(next: FloorCommand): FloorCommand | null {
    if (!(next instanceof PatchObjectsCommand)) return null;
    if (this.mergeKey === null || next.mergeKey !== this.mergeKey) return null;
    // Merge by id: keep OUR prev (oldest), take THEIR next (latest).
    const byId = new Map<string, PatchEntry>();
    for (const entry of this.entries) {
      byId.set(entry.id, { ...entry });
    }
    for (const entry of next.entries) {
      const existing = byId.get(entry.id);
      if (existing === undefined) {
        byId.set(entry.id, { ...entry });
      } else {
        byId.set(entry.id, {
          id: entry.id,
          prev: { ...entry.prev, ...existing.prev },
          next: { ...existing.next, ...entry.next },
        });
      }
    }
    return new PatchObjectsCommand(
      next.label,
      [...byId.values()],
      this.mergeKey,
    );
  }
}

export class InsertObjectsCommand implements FloorCommand {
  readonly label: string;

  private readonly objects: readonly FloorObject[];

  constructor(label: string, objects: readonly FloorObject[]) {
    this.label = label;
    this.objects = objects.map(cloneObject);
  }

  do(target: CommandTarget): void {
    target.upsertObjects(this.objects.map(cloneObject));
  }

  undo(target: CommandTarget): void {
    target.removeObjects(this.objects.map((object) => object.id));
  }
}

export class RemoveObjectsCommand implements FloorCommand {
  readonly label: string;

  private readonly objects: readonly FloorObject[];

  constructor(label: string, objects: readonly FloorObject[]) {
    this.label = label;
    this.objects = objects.map(cloneObject);
  }

  do(target: CommandTarget): void {
    target.removeObjects(this.objects.map((object) => object.id));
  }

  undo(target: CommandTarget): void {
    target.upsertObjects(this.objects.map(cloneObject));
  }
}

export class CompositeCommand implements FloorCommand {
  readonly label: string;

  private readonly commands: readonly FloorCommand[];

  constructor(label: string, commands: readonly FloorCommand[]) {
    this.label = label;
    this.commands = commands;
  }

  do(target: CommandTarget): void {
    for (const command of this.commands) command.do(target);
  }

  undo(target: CommandTarget): void {
    for (let i = this.commands.length - 1; i >= 0; i -= 1) {
      const command = this.commands[i];
      if (command !== undefined) command.undo(target);
    }
  }
}

// ── Factories (capture prev from live state) ───────────────────────────────

/** Translate objects by a delta. `mergeKey` coalesces successive nudges. */
export const makeMoveCommand = (
  objectsById: ObjectsById,
  ids: readonly string[],
  delta: Vec2,
  mergeKey: string | null = null,
): PatchObjectsCommand | null => {
  const entries: PatchEntry[] = [];
  for (const id of ids) {
    const object = objectsById[id];
    if (object === undefined || object.locked) continue;
    entries.push({
      id,
      next: { x: object.x + delta.x, y: object.y + delta.y },
      prev: { x: object.x, y: object.y },
    });
  }
  if (entries.length === 0) return null;
  return new PatchObjectsCommand(`Move ${entries.length}`, entries, mergeKey);
};

/**
 * Generic property transform — resize / rotate / scale / style / lock /
 * hide / layer moves all flow through here. `next` may carry any patchable
 * keys; prev is captured per key.
 */
export const makeTransformCommand = (
  objectsById: ObjectsById,
  entries: readonly { id: string; next: ObjectPatch }[],
  label: string,
  mergeKey: string | null = null,
): PatchObjectsCommand | null => {
  const captured: PatchEntry[] = [];
  for (const entry of entries) {
    const object = objectsById[entry.id];
    if (object === undefined) continue;
    captured.push({
      id: entry.id,
      next: entry.next,
      prev: capturePrev(object, entry.next),
    });
  }
  if (captured.length === 0) return null;
  return new PatchObjectsCommand(label, captured, mergeKey);
};

export const makeInsertCommand = (
  objects: readonly FloorObject[],
  label: string = `Insert ${objects.length}`,
): InsertObjectsCommand => new InsertObjectsCommand(label, objects);

export const makeRemoveCommand = (
  objectsById: ObjectsById,
  ids: readonly string[],
): RemoveObjectsCommand | null => {
  const objects: FloorObject[] = [];
  for (const id of ids) {
    const object = objectsById[id];
    if (object !== undefined) objects.push(object);
  }
  if (objects.length === 0) return null;
  return new RemoveObjectsCommand(`Delete ${objects.length}`, objects);
};

/**
 * Duplicate objects with fresh ids at an offset. Returns the command plus
 * the new ids so the caller can select the copies after execution.
 */
export const makeDuplicateCommand = (
  objectsById: ObjectsById,
  ids: readonly string[],
  offset: Vec2 = { x: 20, y: 20 },
): { command: InsertObjectsCommand; newIds: string[] } | null => {
  const copies: FloorObject[] = [];
  const newIds: string[] = [];
  for (const id of ids) {
    const object = objectsById[id];
    if (object === undefined) continue;
    const copy = cloneObject(object);
    copy.id = createFloorId('obj');
    copy.x += offset.x;
    copy.y += offset.y;
    copies.push(copy);
    newIds.push(copy.id);
  }
  if (copies.length === 0) return null;
  return {
    command: new InsertObjectsCommand(`Duplicate ${copies.length}`, copies),
    newIds,
  };
};

/**
 * Paste clipboard objects with fresh ids at an offset. Same shape as
 * duplicate but sources from a detached clipboard copy (which may reference
 * objects that no longer exist in the store).
 */
export const makePasteCommand = (
  clipboard: readonly FloorObject[],
  offset: Vec2 = { x: 20, y: 20 },
): { command: InsertObjectsCommand; newIds: string[] } | null => {
  if (clipboard.length === 0) return null;
  const copies: FloorObject[] = [];
  const newIds: string[] = [];
  for (const object of clipboard) {
    const copy = cloneObject(object);
    copy.id = createFloorId('obj');
    copy.x += offset.x;
    copy.y += offset.y;
    // Pasted objects detach from any group membership.
    delete copy.groupId;
    copies.push(copy);
    newIds.push(copy.id);
  }
  return {
    command: new InsertObjectsCommand(`Paste ${copies.length}`, copies),
    newIds,
  };
};

/**
 * Group ≥2 objects: inserts a `group` node sized to the selection AABB and
 * stamps each member's `groupId`. The node's id IS the groupId, so members
 * resolve their group without a child-id list (derivable by scan). Returns
 * the group id so callers can select it.
 */
export const makeGroupCommand = (
  objectsById: ObjectsById,
  ids: readonly string[],
  selectionAabb: { x: number; y: number; width: number; height: number },
): { command: CompositeCommand; groupId: string } | null => {
  const members: FloorObject[] = [];
  let maxZ = 0;
  let layerId: string | null = null;
  for (const id of ids) {
    const object = objectsById[id];
    if (object === undefined || object.type === 'group') continue;
    members.push(object);
    if (object.zIndex > maxZ) maxZ = object.zIndex;
    if (layerId === null) layerId = object.layerId;
  }
  if (members.length < 2 || layerId === null) return null;

  const groupId = createFloorId('grp');
  const groupNode: FloorObject = {
    id: groupId,
    type: 'group',
    x: selectionAabb.x,
    y: selectionAabb.y,
    width: selectionAabb.width,
    height: selectionAabb.height,
    rotation: 0,
    scale: 1,
    visible: true,
    locked: false,
    zIndex: maxZ,
    layerId,
    metadata: {},
  };
  const membershipEntries: PatchEntry[] = members.map((object) => ({
    id: object.id,
    next: { groupId },
    prev: { groupId: object.groupId },
  }));
  return {
    command: new CompositeCommand(`Group ${members.length}`, [
      new InsertObjectsCommand('Insert group node', [groupNode]),
      new PatchObjectsCommand('Set group membership', membershipEntries),
    ]),
    groupId,
  };
};

/** Ungroup: remove the group node(s) and clear members' `groupId`. */
export const makeUngroupCommand = (
  objectsById: ObjectsById,
  ids: readonly string[],
): CompositeCommand | null => {
  const groupNodes: FloorObject[] = [];
  const groupIds = new Set<string>();
  for (const id of ids) {
    const object = objectsById[id];
    if (object === undefined) continue;
    if (object.type === 'group') {
      groupNodes.push(object);
      groupIds.add(object.id);
    } else if (object.groupId !== undefined) {
      groupIds.add(object.groupId);
    }
  }
  for (const groupId of groupIds) {
    const node = objectsById[groupId];
    if (node !== undefined && !groupNodes.some((g) => g.id === groupId)) {
      groupNodes.push(node);
    }
  }
  if (groupIds.size === 0) return null;

  const membershipEntries: PatchEntry[] = [];
  for (const object of Object.values(objectsById)) {
    if (object.groupId !== undefined && groupIds.has(object.groupId)) {
      membershipEntries.push({
        id: object.id,
        next: { groupId: undefined },
        prev: { groupId: object.groupId },
      });
    }
  }
  const commands: FloorCommand[] = [];
  if (membershipEntries.length > 0) {
    commands.push(
      new PatchObjectsCommand('Clear group membership', membershipEntries),
    );
  }
  if (groupNodes.length > 0) {
    commands.push(new RemoveObjectsCommand('Remove group nodes', groupNodes));
  }
  if (commands.length === 0) return null;
  return new CompositeCommand('Ungroup', commands);
};

export type ZOrderDirection = 'forward' | 'backward' | 'front' | 'back';

/**
 * Bring-to-front / send-to-back inside each object's layer: renumber the
 * selected block past the extreme sibling, preserving the block's relative
 * render order. Layers whose block already occupies the extreme are skipped;
 * null when every layer is a no-op.
 */
const makeZOrderExtremeCommand = (
  objectsById: ObjectsById,
  layerObjectIds: Readonly<Record<string, readonly string[]>>,
  ids: readonly string[],
  direction: 'front' | 'back',
): PatchObjectsCommand | null => {
  const selected = new Set(ids);
  const layerIds: string[] = [];
  for (const id of ids) {
    const object = objectsById[id];
    if (object === undefined) continue;
    if (!layerIds.includes(object.layerId)) layerIds.push(object.layerId);
  }
  const entries: PatchEntry[] = [];
  for (const layerId of layerIds) {
    const siblings = layerObjectIds[layerId];
    if (siblings === undefined) continue;
    const selectedInOrder = siblings.filter((id) => selected.has(id));
    if (selectedInOrder.length === 0) continue;
    if (selectedInOrder.length === siblings.length) continue; // whole layer
    const count = selectedInOrder.length;
    const atExtreme =
      direction === 'front'
        ? siblings
            .slice(siblings.length - count)
            .every((id, index) => id === selectedInOrder[index])
        : siblings
            .slice(0, count)
            .every((id, index) => id === selectedInOrder[index]);
    if (atExtreme) continue;
    const topId = siblings[siblings.length - 1];
    const bottomId = siblings[0];
    const top = topId === undefined ? undefined : objectsById[topId];
    const bottom = bottomId === undefined ? undefined : objectsById[bottomId];
    if (top === undefined || bottom === undefined) continue;
    for (let index = 0; index < count; index += 1) {
      const id = selectedInOrder[index];
      if (id === undefined) continue;
      const object = objectsById[id];
      if (object === undefined) continue;
      const nextZ =
        direction === 'front'
          ? top.zIndex + 1 + index
          : bottom.zIndex - count + index;
      entries.push({
        id,
        next: { zIndex: nextZ },
        prev: { zIndex: object.zIndex },
      });
    }
  }
  if (entries.length === 0) return null;
  const label = direction === 'front' ? 'Bring to front' : 'Send to back';
  return new PatchObjectsCommand(label, entries);
};

/**
 * Reorder within each object's layer. `forward`/`backward` swap zIndex with
 * the adjacent sibling in render order; `front`/`back` jump the selection to
 * the layer extreme (block order preserved). No-op entries are dropped.
 */
export const makeZOrderCommand = (
  objectsById: ObjectsById,
  layerObjectIds: Readonly<Record<string, readonly string[]>>,
  ids: readonly string[],
  direction: ZOrderDirection,
): PatchObjectsCommand | null => {
  if (direction === 'front' || direction === 'back') {
    return makeZOrderExtremeCommand(
      objectsById,
      layerObjectIds,
      ids,
      direction,
    );
  }
  const selected = new Set(ids);
  const entries: PatchEntry[] = [];
  for (const id of ids) {
    const object = objectsById[id];
    if (object === undefined) continue;
    const siblings = layerObjectIds[object.layerId];
    if (siblings === undefined) continue;
    const index = siblings.indexOf(id);
    if (index === -1) continue;
    const neighborIndex = direction === 'forward' ? index + 1 : index - 1;
    const neighborId = siblings[neighborIndex];
    if (neighborId === undefined) continue; // already at the extreme
    if (selected.has(neighborId)) continue; // moving as a block — keep order
    const neighbor = objectsById[neighborId];
    if (neighbor === undefined) continue;
    // Swap zIndex values; ties get nudged so the swap is observable.
    const objectZ = object.zIndex;
    const neighborZ =
      neighbor.zIndex === object.zIndex
        ? object.zIndex + (direction === 'forward' ? 1 : -1)
        : neighbor.zIndex;
    entries.push({
      id,
      next: { zIndex: neighborZ },
      prev: { zIndex: objectZ },
    });
    entries.push({
      id: neighborId,
      next: { zIndex: objectZ },
      prev: { zIndex: neighbor.zIndex },
    });
  }
  if (entries.length === 0) return null;
  const label = direction === 'forward' ? 'Bring forward' : 'Send backward';
  return new PatchObjectsCommand(label, entries);
};
